/* eslint-disable consistent-return */
import { Popover } from '@headlessui/react';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import DotsIcon from 'images/v5/icons/dots-horizontal.svg';
import Portal from 'layout/atoms/Portal/Portal';
import Button from '../Button/Button';
import styles from './Dropdown.module.scss';
function Dropdown({ open = false, controlled = false, shape = 'max-content', size = 's', fixedWidth = 'content-width', matchTriggerWidth = false, position = 'bottomRight', trigger, closeOnClick = true, scroll = true, onOpen, onClose, onBeforeOpen, children, testId, }) {
    const [active, setActive] = useState(open);
    const triggerRef = useRef(null);
    const panelRef = useRef(null);
    const [panelStyles, setPanelStyles] = useState(undefined);
    const [scrollable, setScrollable] = useState(false);
    const [effectivePosition, setEffectivePosition] = useState(position);
    const calculatePosition = useCallback(() => {
        var _a, _b;
        if (!triggerRef.current)
            return;
        const rect = triggerRef.current.getBoundingClientRect();
        const panelHeight = (_b = (_a = panelRef.current) === null || _a === void 0 ? void 0 : _a.offsetHeight) !== null && _b !== void 0 ? _b : 0;
        const viewportHeight = window.innerHeight;
        const isBottomPos = ['bottomLeft', 'bottomCenter', 'bottomRight'].includes(position);
        const shouldFlip = isBottomPos && panelHeight > 0 && rect.bottom + panelHeight + 12 > viewportHeight;
        const resolvedPosition = shouldFlip
            ? position.replace('bottom', 'top')
            : position;
        setEffectivePosition(resolvedPosition);
        const popoverStyles = Object.assign({ position: 'absolute', top: rect.bottom + window.scrollY, left: rect.left + window.scrollX }, (matchTriggerWidth && { width: rect.width }));
        switch (resolvedPosition) {
            case 'bottomCenter':
                popoverStyles.left = rect.left + window.scrollX + rect.width / 2;
                popoverStyles.transform = 'translateX(-50%)';
                break;
            case 'bottomRight':
                popoverStyles.left = rect.right + window.scrollX;
                popoverStyles.transform = 'translateX(-100%)';
                break;
            case 'topLeft':
                popoverStyles.top = rect.top + window.scrollY;
                popoverStyles.left = rect.left + window.scrollX;
                popoverStyles.transform = 'translateY(-100%)';
                break;
            case 'topCenter':
                popoverStyles.top = rect.top + window.scrollY;
                popoverStyles.left = rect.left + window.scrollX + rect.width / 2;
                popoverStyles.transform = 'translate(-50%, -100%)';
                break;
            case 'topRight':
                popoverStyles.top = rect.top + window.scrollY;
                popoverStyles.left = rect.right + window.scrollX;
                popoverStyles.transform = 'translate(-100%, -100%)';
                break;
            case 'menubar':
                popoverStyles.top = rect.top + window.scrollY + 40;
                popoverStyles.left = rect.right + window.scrollX;
                popoverStyles.transform = 'translate3d(20px, -100%, 0)';
                break;
            default:
                break;
        }
        setPanelStyles(popoverStyles);
    }, [position, matchTriggerWidth]);
    useEffect(() => {
        window.addEventListener('resize', calculatePosition);
        return () => window.removeEventListener('resize', calculatePosition);
    }, [calculatePosition]);
    useEffect(() => {
        if (active) {
            const frame = requestAnimationFrame(() => calculatePosition());
            return () => cancelAnimationFrame(frame);
        }
    }, [active, calculatePosition]);
    useEffect(() => {
        if (open) {
            const frame = requestAnimationFrame(() => calculatePosition());
            return () => cancelAnimationFrame(frame);
        }
    }, [open, calculatePosition]);
    useEffect(() => {
        if (!active && !open)
            return undefined;
        if (controlled)
            return undefined;
        const observer = new MutationObserver(() => calculatePosition());
        if (triggerRef.current) {
            observer.observe(document.body, { childList: true, subtree: true });
        }
        return () => observer.disconnect();
    }, [active, open, controlled, calculatePosition]);
    useEffect(() => {
        const el = panelRef.current;
        if (!el) {
            return () => { };
        }
        const observer = new ResizeObserver(() => {
            const height = el.scrollHeight;
            setScrollable(height > 300);
        });
        observer.observe(el);
        return () => {
            observer.disconnect();
        };
    }, []);
    useEffect(() => {
        if (!controlled || !open)
            return undefined;
        const handleOutsideClick = (e) => {
            var _a, _b;
            if (((_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target)) ||
                ((_b = panelRef.current) === null || _b === void 0 ? void 0 : _b.contains(e.target)))
                return;
            onClose === null || onClose === void 0 ? void 0 : onClose();
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [controlled, open, onClose]);
    const handleOpen = useCallback(() => {
        calculatePosition();
        if (onBeforeOpen) {
            Promise.resolve(onBeforeOpen()).then((canOpen) => {
                if (!canOpen) {
                    setActive(false);
                    return;
                }
                setActive(true);
                onOpen === null || onOpen === void 0 ? void 0 : onOpen();
            });
            return;
        }
        setActive(true);
        onOpen === null || onOpen === void 0 ? void 0 : onOpen();
    }, [calculatePosition, onBeforeOpen, onOpen]);
    if (controlled) {
        return (React.createElement("div", { className: classNames(styles.wrap, { [styles.block]: shape === 'block' }) },
            React.createElement("div", { className: styles.trigger, ref: triggerRef, style: { width: fixedWidth }, "data-test": testId }, trigger),
            open && (React.createElement(Portal, { className: styles.portal },
                React.createElement("div", { ref: panelRef, className: classNames(styles.content, styles[effectivePosition], styles[`size-${size}`], {
                        [styles.scroll]: scroll,
                        [styles.scrollable]: scrollable,
                    }), style: Object.assign(Object.assign({}, panelStyles), { opacity: 1, pointerEvents: 'all' }) },
                    React.createElement("div", null, typeof children === 'function' ? children(() => onClose === null || onClose === void 0 ? void 0 : onClose()) : children))))));
    }
    return (React.createElement(Popover, { className: classNames(styles.wrap, { [styles.block]: shape === 'block' }), as: "div" },
        React.createElement(Popover.Button, { className: `${styles.trigger}`, as: "div", ref: triggerRef, onClick: handleOpen, style: { width: fixedWidth }, "data-test": testId }, trigger || React.createElement(Button, { shape: "rounded", theme: "ghost", leftIcon: React.createElement(DotsIcon, null) })),
        (open || active) && (React.createElement(Portal, { className: styles.portal },
            React.createElement(Popover.Panel, { ref: panelRef, className: classNames(styles.content, styles[effectivePosition], styles[`size-${size}`], {
                    [styles.scroll]: scroll,
                    [styles.scrollable]: scrollable,
                }), style: Object.assign(Object.assign({}, panelStyles), (open && { opacity: 1, pointerEvents: 'all' })) }, closeOnClick
                ? ({ close }) => {
                    const handleClose = () => {
                        close();
                        setActive(false);
                        onClose === null || onClose === void 0 ? void 0 : onClose();
                    };
                    return (React.createElement("div", { role: "button", tabIndex: 0, onClick: handleClose, onKeyDown: (e) => {
                            if (e.key === 'Esc' || e.key === ' ') {
                                handleClose();
                            }
                        } }, typeof children === 'function' ? children(handleClose) : children));
                }
                : ({ close }) => {
                    const handleClose = () => {
                        close();
                        setActive(false);
                        onClose === null || onClose === void 0 ? void 0 : onClose();
                    };
                    return (React.createElement("div", null, typeof children === 'function' ? children(handleClose) : children));
                })))));
}
export default Dropdown;
